import loadable from '@loadable/component';
import { array, bool, func, number, object, shape, string } from 'prop-types';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';

import { propTypes } from '../../util/types';

import { authenticationInProgress, logout } from '../../ducks/auth.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { hasCurrentUserErrors, sendVerificationEmail } from '../../ducks/user.duck';
import { getCartItemCount } from '../../ducks/cart.duck';
import { useIframeDetection } from '../../hooks/useIframeDetection';
import { TopbarIframe } from '../../components';

const Topbar = loadable(() => import(/* webpackChunkName: "Topbar" */ './Topbar/Topbar'));

export const TopbarContainerComponent = props => {
  const {
    authInProgress,
    currentPage,
    currentSearchParams,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    history,
    isAuthenticated,
    authScopes,
    hasGenericError,
    location,
    notificationCount,
    onLogout,
    onManageDisableScrolling,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    onResendVerificationEmail,
    cartItemCount,
    ...rest
  } = props;

  let sellerType =
    currentUser?.attributes?.profile?.publicData?.userType === 'provider'
      ? currentUser?.attributes?.profile?.publicData?.sellerType
        ? currentUser?.attributes?.profile?.publicData?.sellerType
        : 'owner'
      : null;
  const publicData = currentUser?.attributes?.profile?.publicData;
  const sellerOnboardingProgress = publicData?.sellerOnboardingProgress;
  let onboardingComplete = publicData?.onboardingComplete ||
    (sellerOnboardingProgress?.profile && sellerOnboardingProgress?.businessInfo && sellerOnboardingProgress?.stripe);

  const isIframe = useIframeDetection();

  // Resolve dealer-iframe-journey membership as reactive state, decoupled from
  // when StorePage writes the sessionStorage marker. We recompute on mount
  // (covers PDP/checkout, where the marker is already set) and whenever
  // StorePage signals it via the 'dealerIframeJourney:update' event (covers the
  // store landing itself, whose write happens after this child has mounted).
  const [isDealerIframeJourney, setIsDealerIframeJourney] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const compute = () => {
      let inJourney = false;
      if (isIframe) {
        try {
          inJourney = window.sessionStorage.getItem('dealerIframeJourney') === '1';
        } catch (e) {
          inJourney = false;
        }
      }
      setIsDealerIframeJourney(inJourney);
    };
    compute();
    window.addEventListener('dealerIframeJourney:update', compute);
    return () => window.removeEventListener('dealerIframeJourney:update', compute);
  }, [isIframe]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const keys = [
      '--primary-olive',
      '--primary-olive-hover',
      '--primary-camo',
      '--primary-camo-hover',
      '--primary-hunter-orange',
      '--primary-hunter-orange-hover',
      '--marketplaceColor',
      '--marketplaceColorLight',
      '--marketplaceColorDark',
      '--camoColor',
      '--camoColorDark',
      '--oliveColor',
      '--huntingOrange',
      '--huntingOrangeDark',
    ];

    if (isDealerIframeJourney) {
      root.classList.add('dealerIframeJourney');
      root.style.setProperty('--primary-olive', '#4A4A4A');
      root.style.setProperty('--primary-olive-hover', '#3A3A3A');
      root.style.setProperty('--primary-camo', '#5A5A5A');
      root.style.setProperty('--primary-camo-hover', '#444444');
      root.style.setProperty('--primary-hunter-orange', '#6B6B6B');
      root.style.setProperty('--primary-hunter-orange-hover', '#555555');
      root.style.setProperty('--marketplaceColor', '#4A4A4A');
      root.style.setProperty('--marketplaceColorLight', '#6A6A6A');
      root.style.setProperty('--marketplaceColorDark', '#333333');
      root.style.setProperty('--camoColor', '#5A5A5A');
      root.style.setProperty('--camoColorDark', '#444444');
      root.style.setProperty('--oliveColor', '#4A4A4A');
      root.style.setProperty('--huntingOrange', '#6B6B6B');
      root.style.setProperty('--huntingOrangeDark', '#555555');
    } else {
      root.classList.remove('dealerIframeJourney');
      keys.forEach(k => root.style.removeProperty(k));
    }

    return () => {
      root.classList.remove('dealerIframeJourney');
      keys.forEach(k => root.style.removeProperty(k));
    };
  }, [isDealerIframeJourney]);

  useEffect(() => {
    if (
      currentUser &&
      currentPage !== 'OnboardingPage' &&
      currentPage !== 'SellerOnboardingV2Page' &&
      currentPage !== 'SellerOnboardingV2PageStripeReturn' &&
      currentPage !== 'EmailVerificationPage' &&
      !onboardingComplete &&
      publicData?.userType === 'provider'
    ) {
      history.push('/onboarding');
    }
  }, [currentUser]);

  if (isIframe) {
    return <TopbarIframe {...rest} />;
  }

  return (
    <Topbar
      authInProgress={authInProgress}
      currentPage={currentPage}
      currentSearchParams={currentSearchParams}
      currentUser={currentUser}
      currentUserHasListings={currentUserHasListings}
      currentUserHasOrders={currentUserHasOrders}
      history={history}
      isAuthenticated={isAuthenticated}
      authScopes={authScopes}
      location={location}
      notificationCount={notificationCount}
      onLogout={onLogout}
      onManageDisableScrolling={onManageDisableScrolling}
      onResendVerificationEmail={onResendVerificationEmail}
      sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      sendVerificationEmailError={sendVerificationEmailError}
      showGenericError={hasGenericError}
      cartItemCount={cartItemCount}
      {...rest}
    />
  );
};

TopbarContainerComponent.defaultProps = {
  currentPage: null,
  currentSearchParams: null,
  currentUser: null,
  currentUserHasOrders: null,
  notificationCount: 0,
  sendVerificationEmailError: null,
  authScopes: null,
  forceSearch: false,
  cartItemCount: 0,
};

TopbarContainerComponent.propTypes = {
  authInProgress: bool.isRequired,
  currentPage: string,
  currentSearchParams: object,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  onResendVerificationEmail: func.isRequired,
  hasGenericError: bool.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({ state: object }).isRequired,
};

const mapStateToProps = state => {
  // Topbar needs isAuthenticated
  const { isAuthenticated, logoutError, authScopes } = state.auth;
  // Topbar needs user info.
  const {
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserNotificationCount: notificationCount,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = state.user;
  const hasGenericError = !!(logoutError || hasCurrentUserErrors(state));
  return {
    authInProgress: authenticationInProgress(state),
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    notificationCount,
    isAuthenticated,
    authScopes,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    hasGenericError,
    cartItemCount: getCartItemCount(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: historyPush => dispatch(logout(historyPush)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onResendVerificationEmail: () => dispatch(sendVerificationEmail()),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const TopbarContainer = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
)(TopbarContainerComponent);

export default TopbarContainer;
